import React, { useMemo } from "react";
import {
  FileSpreadsheet,
  Loader2,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  List,
} from "lucide-react";
import { Button } from "@shared/ui/button";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { EntityTypeName, ParsedRow, ImportSummary } from "../types";
import { getColumnsForEntityType } from "../entityConfig";

interface PreviewValidateStepProps {
  entityType: EntityTypeName;
  fileName: string;
  rows: ParsedRow[];
  summary: ImportSummary;
  isImporting: boolean;
  onRemoveFile: (onPrevious?: () => void) => void;
  onImport: (onNext?: () => void) => void;
  onBack: (onPrevious?: () => void) => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

// Convert snake_case to camelCase
const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

// Retrieve data value ignoring case differences and mapping snake_case to camelCase
const getRowValue = (data: any, key: string, label: string) => {
  if (!data || typeof data !== "object") return undefined;

  // Direct match
  if (data[key] !== undefined && data[key] !== null) return data[key];
  if (data[label] !== undefined && data[label] !== null) return data[label];

  // Try camelCase version
  const camelKey = toCamelCase(key);
  if (data[camelKey] !== undefined && data[camelKey] !== null)
    return data[camelKey];

  // Try snake_case version
  const snakeKey = key.toLowerCase().replace(/\s+/g, "_");
  if (data[snakeKey] !== undefined && data[snakeKey] !== null)
    return data[snakeKey];

  // Map specific column field aliases for standard headers
  const keyLower = key.toLowerCase();
  const labelLower = label.toLowerCase();

  if (keyLower === "name" || labelLower === "name") {
    const val =
      data.entity_name ??
      data.entityName ??
      data.parent_name ??
      data.parentName ??
      data.configuration_code ??
      data.configurationCode ??
      data.name ??
      data.Name;
    if (val !== undefined && val !== null) return val;
  }

  if (keyLower === "type" || labelLower === "type") {
    const val =
      data.unit_type ??
      data.unitType ??
      data.location_type ??
      data.locationType ??
      data.entity_name ??
      data.entityName ??
      data.type ??
      data.Type;
    if (val !== undefined && val !== null) return val;
  }

  if (keyLower === "status" || labelLower === "status") {
    const val =
      data.occupancy_status ??
      data.occupancyStatus ??
      data.status ??
      data.Status;
    if (val !== undefined && val !== null) return val;
  }

  if (keyLower === "manager" || labelLower === "manager") {
    const val =
      data.ownership_type ??
      data.ownershipType ??
      data.manager ??
      data.Manager;
    if (val !== undefined && val !== null) return val;
  }

  if (keyLower.includes("date") || labelLower.includes("date")) {
    const val =
      data.created_date ??
      data.createdDate ??
      data.date ??
      data.Date;
    if (val !== undefined && val !== null) return val;
  }

  // Case-insensitive fallback scan over all object keys
  for (const k of Object.keys(data)) {
    if (
      k.toLowerCase() === keyLower ||
      toCamelCase(k.toLowerCase()) === keyLower
    ) {
      if (data[k] !== undefined && data[k] !== null) return data[k];
    }
  }

  return undefined;
};

export const PreviewValidateStep: React.FC<PreviewValidateStepProps> = ({
  entityType,
  fileName,
  rows,
  summary,
  isImporting,
  onRemoveFile,
  onImport,
  onBack,
  onNext,
  onPrevious,
}) => {
  const currentColumns = useMemo(() => {
    return getColumnsForEntityType(entityType);
  }, [entityType]);

  const previewRows = rows.slice(0, 8);

  // Array of KPI stats data objects for QuickStatsCard mapping matching screenshot UI
  const kpiStatsData = useMemo(
    () => [
      {
        title: "Total Rows",
        count: summary.totalRows,
        displayValue: summary.totalRows.toString(),
        className: "bg-gray-50/20 border-gray-200 text-gray-900 shadow-none",
      },
      {
        title: "Valid Rows",
        count: summary.validRows,
        displayValue: summary.validRows.toString(),
        className: "bg-green-50/30 border-green-200 text-green-600 shadow-none",
      },
      {
        title: "Invalid Rows",
        count: summary.invalidRows,
        displayValue: summary.invalidRows.toString(),
        className: "bg-red-50/30 border-red-200 text-red-600 shadow-none",
      },
    ],
    [summary],
  );

  // Table Columns using TableColumn interface matching GenericTable
  const columns: TableColumn<any>[] = useMemo(() => {
    const cols: TableColumn<any>[] = [
      {
        key: "rowNumber",
        header: <span className="text-xs font-semibold text-gray-600">#</span>,
        render: (_val: any, record: any) => (
          <span className="text-xs font-medium text-gray-500">
            {record.rowNumber}
          </span>
        ),
      },
    ];

    // Standard columns to keep matching the requested preview table headers
    const colList: Array<{ key: string; label: string; required: boolean }> = [
      { key: "Name", label: "Name", required: true },
      { key: "Type", label: "Type", required: false },
      { key: "Status", label: "Status", required: true },
      { key: "Manager", label: "Manager", required: false },
      { key: "Created Date", label: "Created Date", required: false },
    ];

    colList.forEach((col) => {
      cols.push({
        key: col.key,
        header: (
          <span className="text-xs font-semibold text-gray-600">
            {col.label}
            {col.required && <span className="text-red-500 ml-0.5">*</span>}
          </span>
        ) as any,
        render: (_val: any, record: any) => {
          const rawVal = getRowValue(record.data, col.key, col.label);
          const isMissing =
            col.required &&
            (rawVal === undefined ||
              rawVal === null ||
              String(rawVal).trim() === "");

          if (isMissing) {
            return (
              <span className="text-red-500 italic font-medium">missing</span>
            );
          }

          const isNoData =
            rawVal === undefined ||
            rawVal === null ||
            String(rawVal).trim() === "" ||
            String(rawVal).toLowerCase() === "null" ||
            String(rawVal).toLowerCase() === "undefined";

          return (
            <span
              className={`text-xs ${isNoData ? "text-gray-400 font-medium" : "text-gray-800"}`}
            >
              {isNoData ? "N/A" : String(rawVal)}
            </span>
          );
        },
      });
    });

    cols.push({
      key: "message",
      header: (
        <span className="text-xs font-semibold text-gray-600">Message</span>
      ),
      type: "badge",
      badgeConfig: {
        getVariant: (val: any) =>
          String(val).toLowerCase() === "valid" ||
          String(val).toLowerCase() === "no issue"
            ? "success"
            : "destructive",
        getClassName: (val: any) =>
          String(val).toLowerCase() === "valid" ||
          String(val).toLowerCase() === "no issue"
            ? "bg-green-100 text-green-800 border-none font-semibold text-[11px]"
            : "bg-red-100 text-red-800 border-none font-semibold text-[11px]",
      },
      render: (_val: any, record: any) => {
        const msg =
          record.data?.message ||
          (record.errors && record.errors.length > 0
            ? record.errors.join(", ")
            : record.data?.status === "valid" || record.data?.status === "Valid"
              ? "No issue"
              : String(_val));
        return msg;
      },
    });

    return cols;
  }, [currentColumns, previewRows]);

  // Formatted data for GenericTable
  const tableData = useMemo(() => {
    return previewRows.map((row) => ({
      id: row.id.toString(),
      rowNumber: row.id,
      data: row.data,
      errors: row.errors,
      message:
        row.data?.message ||
        (row.errors && row.errors.length > 0
          ? row.errors.join(", ")
          : row.isValid
            ? "No issue"
            : "Invalid"),
      status: row.data?.status || (row.isValid ? "Valid" : "Invalid"),
    }));
  }, [previewRows]);

  return (
    <div className="space-y-6">
      {/* File info bar */}
      <div className="p-4 border border-gray-200 bg-gray-50/50 rounded-xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center text-green-600">
            <FileSpreadsheet className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">{fileName}</p>
            <p className="text-xs text-gray-500">
              {summary.totalRows} row{summary.totalRows !== 1 ? "s" : ""}{" "}
              detected
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemoveFile(onPrevious)}
          className="text-gray-500 hover:text-red-600 hover:bg-red-50 text-xs font-semibold"
        >
          <Trash2 className="w-3.5 h-3.5 mr-1" />
          Remove
        </Button>
      </div>

      {/* Summary Cards mapped from kpiStatsData */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {kpiStatsData.map((statItem, idx) => (
          <QuickStatsCard key={idx} {...statItem} />
        ))}
      </div>

      {/* Warning when invalid rows exist */}
      {summary.invalidRows > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3 text-amber-800 text-xs font-medium">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <span>
            Invalid rows will be skipped during import. Fix missing required
            fields and re-upload to include them.
          </span>
        </div>
      )}

      {/* Preview Table using GenericTable */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-700">
            Data Preview{" "}
            <span className="font-normal text-gray-400 text-xs">
              (showing first {previewRows.length} row
              {previewRows.length !== 1 ? "s" : ""})
            </span>
          </p>
        </div>

        <GenericTable data={tableData} columns={columns} rowKey="id" />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-6">
        <Button
          variant="outline"
          onClick={() => onBack(onPrevious)}
          disabled={isImporting}
        >
          Back
        </Button>
        <Button
          onClick={() => onImport(onNext)}
          disabled={summary.validRows === 0 || isImporting}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium min-w-36"
        >
          {isImporting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Importing…
            </>
          ) : (
            `Import ${summary.validRows} Record${summary.validRows !== 1 ? "s" : ""}`
          )}
        </Button>
      </div>
    </div>
  );
};
